import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Select, Textarea } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { formatHumanName } from '@medplum/core'
import { useMedplumProfile } from '@medplum/react'
import type { Patient } from '@medplum/fhirtypes'
import { TODO_GROUP_LABEL } from '@/model/middleware-types'
import { readLastBoard, writeLastBoard } from '@/pages/CareCoordination/lastBoardStorage'
import {
  useBoardList,
  useBoardMutations,
  type CellValue,
  type ChecklistStep,
} from '@/pages/Boards/hooks'
import { ChecklistBuilder } from './ChecklistBuilder'
import { TaskFieldForColumn } from './TaskFieldForColumn'
import {
  CREATE_TASK,
  FIELD_LABEL_STYLE,
  RAIL_INPUT_STYLE,
  SECTION_LABEL_STYLE,
} from './createTaskTheme'

type Props = { patient: Patient; opened: boolean; onClose: () => void }

const initials = (display: string): string =>
  display
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?'

/**
 * Files a Care Queue task for the chart's patient without leaving the chart
 * (PD-1588). Two-pane: what the task IS on the left (name, checklist, opening
 * note), where it GOES on the right (board, assignee, priority, due date). The
 * rail's fields come from the selected board's own columns, so a board with
 * custom columns is just as editable here as it is on the queue.
 */
export const CreateTaskModal = ({ patient, opened, onClose }: Props) => {
  const profile = useMedplumProfile()
  const { data, isError: boardsFailed } = useBoardList()
  const boards = useMemo(() => data?.boards ?? [], [data])
  const [boardId, setBoardId] = useState<string | null>(null)
  // One resolution, shared by the fields, the mutations and submit: the explicit
  // pick, else the user's last Care Queue board, else the first one they have.
  const board =
    boards.find(
      (candidate) => candidate.id === (boardId ?? (profile?.id ? readLastBoard(profile.id) : null)),
    ) ?? boards[0]
  const { createItem, updateItem, addNote } = useBoardMutations(board?.id)

  const [name, setName] = useState('')
  // PD-1931 — set when Create is pressed with a requirement missing, so each
  // missing field turns red instead of the button silently doing nothing.
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const showNameError = submitAttempted && !name.trim()
  const boardError =
    submitAttempted && !board
      ? data
        ? 'There are no Care Queue boards yet — create one in Care Coordination first'
        : boardsFailed
          ? "Couldn't load your boards — close this and try again"
          : 'Boards are still loading — try again in a moment'
      : null
  const [values, setValues] = useState<Record<string, CellValue | null>>({})
  const [checklist, setChecklist] = useState<ChecklistStep[]>([])
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const patientRef = useMemo(
    () => ({
      reference: `Patient/${patient.id}`,
      // The same formatter the server uses for the board's patient search, so a
      // chart-created task reads identically to a board-created one.
      display: patient.name?.[0] ? formatHumanName(patient.name[0]) : '',
    }),
    [patient],
  )

  // Every board the app creates carries a patient column, and the built-ins
  // can't be removed — but the type allows a board without one, and such a
  // board would silently file a task with no patient on it while the rail said
  // "locked". Say what will actually happen instead (Greptile !1606).
  const carriesPatient = !!board?.columns.some((column) => column.type === 'patient')

  const pickBoard = (id: string | null) => {
    if (!id) return
    setBoardId(id)
    // Column ids are per-board — carrying values across would write cells that
    // don't exist on the newly picked board.
    setValues({})
    if (profile?.id) writeLastBoard(profile.id, id)
  }

  // The chart action bar keeps this component mounted across a patient switch
  // (same route, no remount), so the draft has to be dropped explicitly or a
  // task typed for one patient could be filed under the next (Greptile !1614).
  useEffect(() => {
    setName('')
    setSubmitAttempted(false)
    setValues({})
    setChecklist([])
    setNote('')
  }, [patient.id])

  const resetForm = () => {
    setName('')
    setSubmitAttempted(false)
    setValues({})
    setChecklist([])
    setNote('')
  }

  // The parent keeps this component mounted, so without this an abandoned draft
  // reappears the next time the modal is opened — including on another
  // patient's chart (Greptile !1606). A failed create deliberately does NOT come
  // through here: it keeps the modal open, with the user's input intact.
  const closeAndReset = () => {
    resetForm()
    onClose()
  }

  const submit = async () => {
    const trimmedName = name.trim()
    if (!trimmedName || !board) {
      setSubmitAttempted(true)
      if (!trimmedName) nameInputRef.current?.focus()
      return
    }
    if (submitting) return
    // No fallback to the board's first group: that could be Completed, filing
    // the task out of the queue it was meant for. Every server-side task creator
    // refuses here too rather than guessing (Greptile !1614).
    const group = board.groups.find((candidate) => candidate.label === TODO_GROUP_LABEL)
    if (!group) {
      showNotification({
        color: 'red',
        message: `"${board.name}" has no ${TODO_GROUP_LABEL} group, so a task can't be filed to it. Pick another board.`,
      })
      return
    }

    setSubmitting(true)

    let itemId: string
    try {
      const created = await createItem.mutateAsync({
        boardId: board.id,
        groupId: group.id,
        name: trimmedName,
      })
      itemId = created.item.id
    } catch {
      // Nothing was created and the mutation hook already reported the error —
      // stay open so the user's input isn't lost.
      setSubmitting(false)
      return
    }

    // The task exists from here on. Retrying would create a duplicate, so a
    // failure below reports an incomplete task rather than rolling back.
    const cells: Record<string, CellValue> = {}
    for (const column of board.columns) {
      if (column.type === 'patient') {
        cells[column.id] = patientRef
        continue
      }
      const value = values[column.id]
      if (value !== null && value !== undefined && value !== '') cells[column.id] = value
    }

    try {
      if (Object.keys(cells).length > 0 || checklist.length > 0) {
        await updateItem.mutateAsync({
          itemId,
          ...(Object.keys(cells).length > 0 ? { values: cells } : {}),
          ...(checklist.length > 0 ? { checklist } : {}),
        })
      }
      const trimmedNote = note.trim()
      if (trimmedNote) await addNote.mutateAsync({ itemId, text: trimmedNote })
    } catch {
      // The task is already on the board, so closing is right — pressing Create
      // again would duplicate it. The warning rides a notification, which
      // outlives the modal, rather than an alert inside it.
      setSubmitting(false)
      showNotification({
        color: 'orange',
        message:
          "Task created, but some details couldn't be saved. Open it on the board to finish it.",
      })
      closeAndReset()
      return
    }

    setSubmitting(false)
    showNotification({ color: 'green', message: 'Task created' })
    // The board choice is deliberately kept by resetForm: it's the user's
    // remembered board, not task content.
    closeAndReset()
  }

  return (
    <Modal
      opened={opened}
      onClose={closeAndReset}
      size="900px"
      padding={0}
      radius={16}
      withCloseButton={false}
      styles={{
        // The checklist can grow without limit, so the modal is capped to the
        // viewport and the panes scroll inside it — otherwise a long checklist
        // pushes the footer (and its Create button) off screen entirely.
        body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
        header: { display: 'none' },
        content: {
          overflow: 'hidden',
          border: '1px solid #DCE3EA',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 80px)',
        },
      }}
    >
      {/* Header: what this is, who it's about, and the way out. */}
      <div
        className="flex flex-none items-center gap-3"
        style={{ padding: '18px 22px', borderBottom: `1px solid ${CREATE_TASK.rule}` }}
      >
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 700, color: CREATE_TASK.ink }}>
          Create task
        </h2>
        <div style={{ width: 1, height: 20, background: CREATE_TASK.divider }} />
        <div
          className="flex h-7 items-center gap-2"
          style={{
            padding: '0 10px 0 4px',
            borderRadius: 999,
            background: CREATE_TASK.patientChipBg,
            border: `1px solid ${CREATE_TASK.patientChipBorder}`,
          }}
        >
          <span
            className="flex h-5 w-5 items-center justify-center rounded-full"
            style={{
              background: CREATE_TASK.patientAvatar,
              color: '#FFFFFF',
              fontSize: 9.5,
              fontWeight: 700,
            }}
          >
            {initials(patientRef.display)}
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: CREATE_TASK.patientChipText }}>
            {patientRef.display}
          </span>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={closeAndReset}
          className="ml-auto h-8 w-8 shrink-0 cursor-pointer rounded-lg border-none bg-transparent leading-none"
          style={{ color: CREATE_TASK.subtle, fontSize: 19 }}
        >
          ×
        </button>
      </div>

      <div className="flex items-stretch" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Left: what the task is. */}
        <div
          data-create-task-pane="left"
          className="flex min-w-0 flex-1 flex-col gap-5"
          style={{ padding: '24px 24px 26px', overflowY: 'auto', minHeight: 0 }}
        >
          <div className="flex flex-col gap-1.5">
            <input
              ref={nameInputRef}
              type="text"
              aria-label="Task name"
              aria-invalid={showNameError || undefined}
              placeholder="Task name"
              required
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
              className="h-10 border-none bg-transparent p-0 outline-none"
              style={{
                borderBottom: `2px solid ${showNameError ? CREATE_TASK.error : CREATE_TASK.rule}`,
                fontSize: 23,
                fontWeight: 700,
                letterSpacing: '-0.015em',
                color: CREATE_TASK.ink,
              }}
            />
            {showNameError ? (
              <span
                role="alert"
                style={{ fontSize: 12.5, fontWeight: 600, color: CREATE_TASK.error }}
              >
                Enter a task name — it&apos;s required and appears on the coordinator&apos;s board
              </span>
            ) : (
              <span style={{ fontSize: 12.5, color: CREATE_TASK.subtle }}>
                Required · appears on the coordinator&apos;s board
              </span>
            )}
          </div>

          <ChecklistBuilder steps={checklist} onChange={setChecklist} />

          <div className="flex flex-col gap-2.5">
            <div style={SECTION_LABEL_STYLE}>First note</div>
            <Textarea
              aria-label="First note"
              rows={4}
              placeholder="Context the coordinator needs before they start — last visit, what the patient already knows, who to call."
              value={note}
              onChange={(event) => setNote(event.currentTarget.value)}
              styles={{
                input: {
                  borderRadius: 11,
                  borderColor: CREATE_TASK.line,
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: CREATE_TASK.ink,
                },
              }}
            />
          </div>
        </div>

        {/* Right: where the task goes. */}
        <div
          data-create-task-pane="right"
          className="flex w-[318px] flex-none flex-col gap-4"
          style={{
            padding: '22px 22px 26px',
            background: CREATE_TASK.railBg,
            borderLeft: `1px solid ${CREATE_TASK.hairline}`,
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          <div style={SECTION_LABEL_STYLE}>Properties</div>

          <Select
            label="Board"
            data={boards.map((candidate) => ({ value: candidate.id, label: candidate.name }))}
            value={board?.id ?? null}
            onChange={pickBoard}
            allowDeselect={false}
            error={boardError}
            errorProps={{ role: 'alert', style: { color: CREATE_TASK.error } }}
            styles={{
              label: FIELD_LABEL_STYLE,
              // The rail's inline border would otherwise mask Mantine's error border.
              input: boardError
                ? { ...RAIL_INPUT_STYLE, borderColor: CREATE_TASK.error }
                : RAIL_INPUT_STYLE,
            }}
          />

          {board?.columns.map((column) => (
            <TaskFieldForColumn
              key={column.id}
              column={column}
              value={values[column.id] ?? null}
              onChange={(value) => setValues((previous) => ({ ...previous, [column.id]: value }))}
            />
          ))}

          {/* The patient isn't a choice here — the chart picked it. Shown so the
              rail still accounts for every cell the task will carry. */}
          <div
            className="flex items-center gap-2.5"
            style={{ marginTop: 2, paddingTop: 14, borderTop: `1px solid ${CREATE_TASK.divider}` }}
          >
            <span
              className="flex flex-none items-center justify-center rounded-full"
              style={{
                width: 26,
                height: 26,
                background: CREATE_TASK.patientAvatar,
                color: '#FFFFFF',
                fontSize: 10.5,
                fontWeight: 700,
              }}
            >
              {initials(patientRef.display)}
            </span>
            <div className="flex flex-col">
              <span style={{ fontSize: 13, fontWeight: 600, color: CREATE_TASK.ink }}>
                {patientRef.display}
              </span>
              <span
                style={{ fontSize: 12, color: carriesPatient ? CREATE_TASK.subtle : '#B54708' }}
              >
                {carriesPatient
                  ? 'Patient · locked'
                  : `This board has no Patient column, so the task won't be linked to ${patientRef.display}.`}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        className="flex flex-none items-center justify-end gap-2.5"
        style={{ padding: '14px 22px', borderTop: `1px solid ${CREATE_TASK.hairline}` }}
      >
        <Button
          variant="default"
          onClick={closeAndReset}
          styles={{
            root: { height: 40, borderRadius: 10, borderColor: CREATE_TASK.line },
            label: { fontSize: 14, fontWeight: 600, color: CREATE_TASK.body },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          loading={submitting}
          styles={{
            root: {
              height: 40,
              borderRadius: 10,
              background: CREATE_TASK.blueDeep,
              border: '1px solid #17456D',
            },
            label: { fontSize: 14, fontWeight: 700 },
          }}
        >
          Create task
        </Button>
      </div>
    </Modal>
  )
}
